import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  CreatePlatformCapabilityInput,
  CreatePlatformMembershipInput,
  platformCoreNetworkApi,
  PlatformCapability,
  PlatformCapabilityMetadata,
  PlatformMember,
  PlatformMembership,
} from '../../../api/platform/coreNetwork';
import PortalAccessCard from '../components/PortalAccessCard';
import TalentPasswordManagementCard from '../components/TalentPasswordManagementCard';
import TalentVerificationCard, { VerificationActions, VerificationStatusBadge } from '../components/TalentVerificationCard';
import { buildCoreAssetUrl } from '../../../config/environment';
import { useAsyncData } from '../hooks/useAsyncData';
import MemberImageEditor from '../components/MemberImageEditor';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FeedbackBanner,
  FieldGroup,
  InlineActions,
  KeyValueGrid,
  LoadingState,
  MvpCard,
  MvpTalentLayout,
  StatusBadge,
} from '../components/MvpTalentUi';
import { EMPTY_VALUE, formatDateTime, formatEmptyValue, formatList, toInitials } from '../utils/format';
import {
  CAPABILITY_CATALOGUE,
  CAPABILITY_GROUPS,
  DATA_ENTRY_TASK_OPTIONS,
  DECORATION_METHOD_OPTIONS,
  FILE_TYPE_OPTIONS,
  LOGISTICS_SERVICE_OPTIONS,
  RATE_TYPE_OPTIONS as CAPABILITY_RATE_TYPE_OPTIONS,
  TURNAROUND_UNIT_OPTIONS,
  findCapabilityDefinition,
  getCapabilityGroupLabel,
  getCapabilityLabel,
} from '../config/capabilities';
import { VerificationAction, buildVerificationPayload } from '../config/verification';
import { useAuth } from '../../../auth/AuthProvider';
import { isAdministratorRole } from '../../../auth/role';

const MEMBERSHIP_STATUS_OPTIONS = ['active', 'pending', 'paused', 'rejected', 'suspended'];
const MEMBERSHIP_ROLE_OPTIONS = ['provider', 'designer', 'decorator', 'vendor', 'partner'];
const MEMBERSHIP_VISIBILITY_OPTIONS = ['visible', 'private', 'internal', 'marketplace', 'featured'];
const REQUEST_TYPE_OPTIONS = ['artwork-proof', 'vector-conversion', 'digitizing', 'proof-mockup'];
const MEMBERSHIP_RATE_TYPE_OPTIONS = ['fixed', 'hourly', 'per_unit', 'quoted'];
const createCapabilityFormState = (capability?: PlatformCapability | null) => {
  const definition = findCapabilityDefinition(capability?.capabilityType);
  const metadata = capability?.metadataJson || {};
  return {
    capabilityId: capability?.id || null,
    capabilityType: capability?.capabilityType || '',
    capabilityGroup: capability?.capabilityGroup || definition?.group || '',
    isActive: capability?.isActive !== false,
    displayName: capability?.displayName || '',
    notes: capability?.notes || '',
    description: capability?.description || '',
    turnaroundValue: metadata.turnaround?.value !== undefined && metadata.turnaround?.value !== null ? String(metadata.turnaround.value) : '',
    turnaroundUnit: metadata.turnaround?.unit || 'business_days',
    rushAvailable: Boolean(metadata.turnaround?.rushAvailable),
    rushValue: metadata.turnaround?.rushValue !== undefined && metadata.turnaround?.rushValue !== null ? String(metadata.turnaround.rushValue) : '',
    minimumLeadTime: metadata.turnaround?.minimumLeadTime !== undefined && metadata.turnaround?.minimumLeadTime !== null ? String(metadata.turnaround.minimumLeadTime) : '',
    maximumActiveJobs: metadata.capacity?.maximumActiveJobs !== undefined && metadata.capacity?.maximumActiveJobs !== null ? String(metadata.capacity.maximumActiveJobs) : '',
    pricingEnabled: Boolean(metadata.pricing?.enabled),
    rateType: metadata.pricing?.rateType || definition?.defaultRateType || 'quoted',
    rateAmount: metadata.pricing?.amount !== undefined && metadata.pricing?.amount !== null ? String(metadata.pricing.amount) : '',
    currency: metadata.pricing?.currency || 'CAD',
    minimumCharge: metadata.pricing?.minimumCharge !== undefined && metadata.pricing?.minimumCharge !== null ? String(metadata.pricing.minimumCharge) : '',
    acceptedFileTypes: metadata.acceptedFileTypes || [],
    outputFileTypes: metadata.outputFileTypes || [],
    colourMatchingSupported: Boolean(metadata.colourMatchingSupported),
    complexArtworkSupported: Boolean(metadata.complexArtworkSupported),
    decorationMethods: metadata.decorationMethods || [],
    minimumQuantity: metadata.capacity?.minimumQuantity !== undefined && metadata.capacity?.minimumQuantity !== null ? String(metadata.capacity.minimumQuantity) : '',
    maximumQuantity: metadata.capacity?.maximumQuantity !== undefined && metadata.capacity?.maximumQuantity !== null ? String(metadata.capacity.maximumQuantity) : '',
    sampleProductionAvailable: Boolean(metadata.sampleProductionAvailable),
    productionLocation: metadata.productionLocation || '',
    serviceOptions: metadata.serviceOptions || [],
    maximumOrderVolume: metadata.capacity?.maximumOrderVolume !== undefined && metadata.capacity?.maximumOrderVolume !== null ? String(metadata.capacity.maximumOrderVolume) : '',
    taskTypes: metadata.taskTypes || [],
    hourlyCapacity: metadata.capacity?.hourlyCapacity !== undefined && metadata.capacity?.hourlyCapacity !== null ? String(metadata.capacity.hourlyCapacity) : '',
    softwareExperience: metadata.softwareExperience || '',
  };
};

const createMembershipFormState = (membership?: PlatformMembership | null) => ({
  membershipId: membership?.id || null,
  instanceId: membership?.instanceId ? String(membership.instanceId) : '',
  status: membership?.status || 'active',
  role: membership?.role || 'provider',
  visibility: membership?.visibility || 'visible',
  allowedRequestTypesJson: membership?.allowedRequestTypesJson || [],
  rateType: String(membership?.rateJson?.type || ''),
  rateAmount: membership?.rateJson?.amount !== undefined && membership?.rateJson?.amount !== null ? String(membership.rateJson.amount) : '',
  rateCurrency: String(membership?.rateJson?.currency || 'CAD'),
  reason: '',
});

const toggleSelection = (values: string[], value: string) => (
  values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value]
);

const buildCapabilitySource = (capability: PlatformCapability) => {
  const source = String(capability.source || '').toLowerCase();
  if (!capability.membershipId && ['member', 'global', 'global_profile', 'profile', 'direct'].includes(source)) {
    return 'Global profile';
  }
  if (capability.membershipId) return `Membership #${capability.membershipId}`;
  if (capability.instanceId) return `Instance #${capability.instanceId}`;
  if (source) return source.replace(/[_-]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  return 'Global profile';
};

const getCapabilityCategoryLabel = (capability: PlatformCapability) => {
  const definition = findCapabilityDefinition(capability.capabilityType);
  return getCapabilityGroupLabel(capability.capabilityGroup || definition?.group);
};

const toNumberOrNull = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatTurnaroundSummary = (capability: PlatformCapability) => {
  const turnaround = capability.metadataJson?.turnaround;
  if (!turnaround?.value || !turnaround.unit) return EMPTY_VALUE;
  const unitLabel = TURNAROUND_UNIT_OPTIONS.find((unit) => unit.value === turnaround.unit)?.label.toLowerCase() || turnaround.unit.replace(/_/g, ' ');
  return `${turnaround.value} ${unitLabel}`;
};

const formatPricingSummary = (capability: PlatformCapability) => {
  const pricing = capability.metadataJson?.pricing;
  if (!pricing?.enabled) return 'Not configured';
  const rateLabel = CAPABILITY_RATE_TYPE_OPTIONS.find((rate) => rate.value === pricing.rateType)?.label.toLowerCase() || pricing.rateType || 'quoted';
  if (pricing.rateType === 'quoted') return 'Quoted';
  if (pricing.amount === undefined || pricing.amount === null) return rateLabel;
  return `$${pricing.amount} ${pricing.currency || 'CAD'} ${rateLabel}`;
};

const buildCapabilityMetadata = (form: ReturnType<typeof createCapabilityFormState>, definition: ReturnType<typeof findCapabilityDefinition>): PlatformCapabilityMetadata => ({
  turnaround: definition?.supportsTurnaround ? {
    value: toNumberOrNull(form.turnaroundValue),
    unit: form.turnaroundUnit as PlatformCapabilityMetadata['turnaround'] extends infer T ? T extends { unit?: infer U } ? U : never : never,
    rushAvailable: definition?.supportsRush ? form.rushAvailable : false,
    rushValue: definition?.supportsRush && form.rushAvailable ? toNumberOrNull(form.rushValue) : null,
    minimumLeadTime: toNumberOrNull(form.minimumLeadTime),
  } : undefined,
  pricing: definition?.supportsPricing ? {
    enabled: form.pricingEnabled,
    rateType: form.rateType || definition.defaultRateType,
    amount: toNumberOrNull(form.rateAmount),
    currency: form.currency.trim() || 'CAD',
    minimumCharge: toNumberOrNull(form.minimumCharge),
  } : undefined,
  capacity: {
    maximumActiveJobs: toNumberOrNull(form.maximumActiveJobs),
    minimumQuantity: definition?.supportsQuantityRange || definition?.supportsDecorationMethods ? toNumberOrNull(form.minimumQuantity) : null,
    maximumQuantity: definition?.supportsQuantityRange || definition?.supportsDecorationMethods ? toNumberOrNull(form.maximumQuantity) : null,
    maximumOrderVolume: definition?.supportsLogisticsOptions ? toNumberOrNull(form.maximumOrderVolume) : null,
    hourlyCapacity: definition?.supportsDataEntryOptions ? toNumberOrNull(form.hourlyCapacity) : null,
  },
  acceptedFileTypes: definition?.supportsFileTypes ? form.acceptedFileTypes : [],
  outputFileTypes: definition?.supportsFileTypes ? form.outputFileTypes : [],
  decorationMethods: definition?.supportsDecorationMethods ? form.decorationMethods : [],
  serviceOptions: definition?.supportsLogisticsOptions ? form.serviceOptions : [],
  taskTypes: definition?.supportsDataEntryOptions ? form.taskTypes : [],
  productionLocation: definition?.supportsDecorationMethods ? form.productionLocation.trim() || null : null,
  softwareExperience: definition?.supportsDataEntryOptions ? form.softwareExperience.trim() || null : null,
  colourMatchingSupported: definition?.supportsFileTypes ? form.colourMatchingSupported : false,
  complexArtworkSupported: definition?.supportsFileTypes ? form.complexArtworkSupported : false,
  sampleProductionAvailable: definition?.supportsDecorationMethods ? form.sampleProductionAvailable : false,
});

const buildRateSummary = (membership: PlatformMembership) => {
  if (!membership.rateJson) return EMPTY_VALUE;
  const type = formatEmptyValue(String(membership.rateJson.type || ''));
  const amount = membership.rateJson.amount !== undefined && membership.rateJson.amount !== null
    ? String(membership.rateJson.amount)
    : EMPTY_VALUE;
  const currency = formatEmptyValue(String(membership.rateJson.currency || ''));
  if (amount === EMPTY_VALUE && currency === EMPTY_VALUE) {
    return type;
  }
  return `${type}: ${amount} ${currency}`.trim();
};

const renderMemberBannerPosition = (focalPoint?: PlatformMember['bannerFocalPointJson'] | null) => {
  if (!focalPoint) return { objectPosition: 'center center' };
  const x = clampPercentage(focalPoint.x);
  const y = clampPercentage(focalPoint.y);
  return { objectPosition: `${x}% ${y}%` };
};

const clampPercentage = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 50;
  return Math.min(100, Math.max(0, value * 100));
};

const TalentDetailPage: React.FC = () => {
  const { memberId = '' } = useParams();
  const { user, isAdministrator } = useAuth();
  const numericMemberId = Number(memberId);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(
    (location.state as { feedback?: { tone: 'success' | 'error'; message: string } } | null)?.feedback || null
  );
  const [statusDialog, setStatusDialog] = useState<{ action: 'suspend' | 'reactivate' | 'revokePortal' } | null>(null);
  const [capabilityModalOpen, setCapabilityModalOpen] = useState(false);
  const addCapabilityButtonRef = useRef<HTMLButtonElement | null>(null);
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const [capabilitySubmitting, setCapabilitySubmitting] = useState(false);
  const [membershipSubmitting, setMembershipSubmitting] = useState(false);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [capabilityForm, setCapabilityForm] = useState(createCapabilityFormState());
  const [membershipForm, setMembershipForm] = useState(createMembershipFormState());
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [imageEditorMode, setImageEditorMode] = useState<'profile' | 'banner' | null>(null);
  const [imageActionBusy, setImageActionBusy] = useState<'profile' | 'banner' | null>(null);
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const [bannerImageBroken, setBannerImageBroken] = useState(false);
  const tab = searchParams.get('tab') || 'overview';
  const selectedCapabilityDefinition = findCapabilityDefinition(capabilityForm.capabilityType);
  const canManageTalentCredentials = isAdministrator || isAdministratorRole(user?.role);

  const { data, loading, error, reload } = useAsyncData(async (signal) => {
    const [member, memberships, activity, portalSessions, instances, settings, capabilitiesResult] = await Promise.all([
      platformCoreNetworkApi.getMember(memberId, { signal }),
      platformCoreNetworkApi.getMemberMemberships(memberId, { signal }),
      platformCoreNetworkApi.getMemberActivity(memberId, { signal }),
      platformCoreNetworkApi.listPortalSessions({ memberId, page: 1, limit: 10 }, { signal }),
      platformCoreNetworkApi.listInstances({ page: 1, limit: 100 }, { signal }),
      platformCoreNetworkApi.getPlatformSettings({ signal }),
      platformCoreNetworkApi.listMemberCapabilities(memberId, { signal })
        .then((capabilities) => ({ capabilities, error: null as string | null }))
        .catch((capabilityLoadError: any) => ({
          capabilities: [] as PlatformCapability[],
          error: capabilityLoadError?.message || 'Detailed capability records are not currently available.',
        })),
    ]);

    return {
      member,
      memberships,
      activity,
      portalSessions,
      instances,
      settings,
      capabilities: capabilitiesResult.capabilities,
      capabilitiesError: capabilitiesResult.error,
    };
  }, [memberId]);

  const tabs = useMemo(
    () => ['overview', 'capabilities', 'memberships', 'work-history', 'portal-access', 'security', 'audit-history'],
    []
  );

  useEffect(() => {
    setProfileImageBroken(false);
  }, [data?.member.profileImageUrl]);

  useEffect(() => {
    setBannerImageBroken(false);
  }, [data?.member.bannerImageUrl]);

  const closeCapabilityModal = useCallback(() => {
    if (capabilitySubmitting) return;
    setCapabilityModalOpen(false);
    window.setTimeout(() => addCapabilityButtonRef.current?.focus(), 0);
  }, [capabilitySubmitting]);

  const openCapabilityModal = (capability?: PlatformCapability | null) => {
    setCapabilityError(null);
    setCapabilityForm(createCapabilityFormState(capability));
    setCapabilityModalOpen(true);
  };

  const openMembershipModal = (membership?: PlatformMembership | null) => {
    setMembershipError(null);
    setMembershipForm(createMembershipFormState(membership));
    setMembershipModalOpen(true);
  };

  const handleStatusMutation = async (action: NonNullable<typeof statusDialog>['action'], reason: string) => {
    try {
      if (action === 'revokePortal') {
        await platformCoreNetworkApi.revokePortalAccess(memberId, reason);
      } else {
        await platformCoreNetworkApi.updateMemberStatus(memberId, {
          status: action === 'suspend' ? 'suspended' : 'active',
          reason,
        });
      }
      setFeedback({
        tone: 'success',
        message: action === 'reactivate' ? 'Talent approved. Work Portal access has not yet been enabled.' : 'Talent profile updated.',
      });
      await reload();
    } catch (mutationError: any) {
      setFeedback({ tone: 'error', message: mutationError?.message || 'Unable to update this talent profile.' });
      throw mutationError;
    }
  };

  useEffect(() => {
    if (!capabilityModalOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !capabilitySubmitting) {
        closeCapabilityModal();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [capabilityModalOpen, capabilitySubmitting, closeCapabilityModal]);

  const handleVerificationMutation = async ({ action, reason }: { action: VerificationAction; reason: string }) => {
    setVerificationSubmitting(true);
    try {
      await platformCoreNetworkApi.updateMemberVerification(memberId, buildVerificationPayload(action, reason));
      const successMessage = action === 'approve'
        ? 'Talent approved successfully. Work Portal access can now be enabled.'
        : action === 'reject'
          ? 'Talent rejected successfully. Work Portal access remains blocked.'
          : 'Talent verification revoked. Review Work Portal access separately.';
      setFeedback({ tone: 'success', message: successMessage });
      await reload();
    } catch (mutationError: any) {
      setFeedback({ tone: 'error', message: mutationError?.message || 'Unable to update talent verification.' });
      throw mutationError;
    } finally {
      setVerificationSubmitting(false);
    }
  };

  const handleMemberImageUpdate = async (
    mode: 'profile' | 'banner',
    mutation: () => Promise<unknown>,
    successMessage: string
  ) => {
    setImageActionBusy(mode);
    try {
      await mutation();
      setFeedback({ tone: 'success', message: successMessage });
      await reload();
    } catch (mutationError: any) {
      setFeedback({ tone: 'error', message: mutationError?.message || 'Unable to update this image.' });
      throw mutationError;
    } finally {
      setImageActionBusy(null);
    }
  };

  const updateCapabilityActiveState = async (capability: PlatformCapability, nextActive: boolean) => {
    try {
      await platformCoreNetworkApi.updateMemberCapability(memberId, capability.id, {
        isActive: nextActive,
      });
      setFeedback({
        tone: 'success',
        message: nextActive ? `${getCapabilityLabel(capability.capabilityType)} reactivated successfully.` : `${getCapabilityLabel(capability.capabilityType)} deactivated successfully.`,
      });
      await reload();
    } catch (mutationError: any) {
      setFeedback({ tone: 'error', message: mutationError?.message || 'Unable to update the capability status.' });
    }
  };

  const submitCapability = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCapabilitySubmitting(true);
    setCapabilityError(null);

    const definition = findCapabilityDefinition(capabilityForm.capabilityType);
    if (!definition) {
      setCapabilityError('Choose a valid capability before saving.');
      setCapabilitySubmitting(false);
      return;
    }

    const duplicateCapability = data?.capabilities.find((capability) => (
      capability.capabilityType === definition.value
      && capability.id !== capabilityForm.capabilityId
      && !capability.membershipId
    ));

    if (duplicateCapability) {
      setCapabilityError(
        duplicateCapability.isActive === false
          ? `${definition.label} already exists as inactive. Edit or reactivate the existing capability instead of creating a duplicate.`
          : `This talent already has the ${definition.label} capability.`
      );
      setCapabilitySubmitting(false);
      return;
    }

    const payload: CreatePlatformCapabilityInput = {
      capabilityType: definition.value,
      capabilityGroup: definition.group,
      displayName: capabilityForm.displayName.trim() || null,
      description: capabilityForm.description.trim() || null,
      metadataJson: buildCapabilityMetadata(capabilityForm, definition),
      isActive: capabilityForm.isActive,
      notes: capabilityForm.notes.trim() || null,
    };

    try {
      if (capabilityForm.capabilityId) {
        await platformCoreNetworkApi.updateMemberCapability(memberId, capabilityForm.capabilityId, payload);
        setFeedback({ tone: 'success', message: `${definition.label} was updated for this talent profile.` });
      } else {
        await platformCoreNetworkApi.createMemberCapability(memberId, payload);
        setFeedback({ tone: 'success', message: `${definition.label} was added to this talent profile.` });
      }
      setCapabilityModalOpen(false);
      await reload();
    } catch (mutationError: any) {
      setCapabilityError(mutationError?.message || 'Unable to save the capability.');
    } finally {
      setCapabilitySubmitting(false);
    }
  };

  const submitMembership = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMembershipSubmitting(true);
    setMembershipError(null);

    const payload: CreatePlatformMembershipInput = {
      memberId: numericMemberId,
      status: membershipForm.status,
      role: membershipForm.role.trim() || null,
      visibility: membershipForm.visibility.trim() || null,
      allowedRequestTypesJson: membershipForm.allowedRequestTypesJson,
      rateJson: membershipForm.rateType || membershipForm.rateAmount || membershipForm.rateCurrency
        ? {
            type: membershipForm.rateType.trim() || null,
            amount: membershipForm.rateAmount ? Number(membershipForm.rateAmount) : null,
            currency: membershipForm.rateCurrency.trim() || null,
          }
        : null,
      reason: membershipForm.reason.trim() || null,
    };

    try {
      if (membershipForm.membershipId) {
        await platformCoreNetworkApi.updateInstanceMembership(
          membershipForm.instanceId,
          membershipForm.membershipId,
          payload
        );
        setFeedback({ tone: 'success', message: 'Membership updated successfully.' });
      } else {
        await platformCoreNetworkApi.createInstanceMembership(membershipForm.instanceId, payload);
        setFeedback({ tone: 'success', message: 'Membership added successfully.' });
      }
      setMembershipModalOpen(false);
      await reload();
    } catch (mutationError: any) {
      setMembershipError(mutationError?.message || 'Unable to save the membership.');
    } finally {
      setMembershipSubmitting(false);
    }
  };

  const renderCapabilitiesSection = () => {
    if (!data) return null;

    return (
      <MvpCard title="Capabilities">
        <InlineActions>
          <button type="button" className="mvp-button" ref={addCapabilityButtonRef} onClick={() => openCapabilityModal()}>
            Add capability
          </button>
        </InlineActions>
        {data.capabilitiesError ? (
          <FeedbackBanner tone="error" message={data.capabilitiesError} />
        ) : null}
        {data.capabilities.length ? (
          <DataTable columns={['Capability', 'Category', 'Turnaround', 'Pricing', 'Status', 'Source', 'Actions']}>
            {data.capabilities.map((capability) => (
              <tr key={capability.id}>
                <td>
                  <strong>{formatEmptyValue(capability.displayName || getCapabilityLabel(capability.capabilityType))}</strong>
                  {capability.description ? <div className="mvp-table__muted">{capability.description}</div> : null}
                </td>
                <td>{getCapabilityCategoryLabel(capability)}</td>
                <td>{formatTurnaroundSummary(capability)}</td>
                <td>{formatPricingSummary(capability)}</td>
                <td><StatusBadge status={capability.isActive === false ? 'inactive' : 'active'} /></td>
                <td>{buildCapabilitySource(capability)}</td>
                <td>
                  <InlineActions>
                    <button type="button" className="mvp-button mvp-button--ghost" onClick={() => openCapabilityModal(capability)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="mvp-button mvp-button--ghost"
                      onClick={() => updateCapabilityActiveState(capability, capability.isActive === false)}
                    >
                      {capability.isActive === false ? 'Reactivate' : 'Deactivate'}
                    </button>
                  </InlineActions>
                </td>
              </tr>
            ))}
          </DataTable>
        ) : (
          <>
            {data.member.capabilityTypes?.length ? (
              <p><strong>Current summary:</strong> {formatList(data.member.capabilityTypes)}</p>
            ) : null}
            <EmptyState
              title="No capabilities"
              message="This talent is not yet eligible for matching. Add at least one capability."
              action={(
                <button type="button" className="mvp-button" ref={addCapabilityButtonRef} onClick={() => openCapabilityModal()}>
                  Add capability
                </button>
              )}
            />
          </>
        )}
      </MvpCard>
    );
  };

  const renderMembershipsSection = () => {
    if (!data) return null;

    return (
      <MvpCard title="Instance memberships">
        <InlineActions>
          <button type="button" className="mvp-button" onClick={() => openMembershipModal()}>
            Add membership
          </button>
        </InlineActions>
        {data.memberships.length ? (
          <DataTable columns={['Instance', 'Status', 'Role', 'Visibility', 'Request types', 'Rate', 'Actions']}>
            {data.memberships.map((membership) => (
              <tr key={membership.id}>
                <td>
                  <Link to={`/command-center/mvp-talent/instances/${membership.instanceId}`}>
                    {membership.instanceName || membership.instanceUniqueId || `Instance ${membership.instanceId}`}
                  </Link>
                </td>
                <td><StatusBadge status={membership.status} /></td>
                <td>{formatEmptyValue(membership.role)}</td>
                <td>{formatEmptyValue(membership.visibility)}</td>
                <td>{formatList(membership.allowedRequestTypesJson)}</td>
                <td>{buildRateSummary(membership)}</td>
                <td>
                  <button type="button" className="mvp-button mvp-button--ghost" onClick={() => openMembershipModal(membership)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </DataTable>
        ) : (
          <EmptyState
            title="No memberships"
            message="This global talent profile is not currently connected to any instance."
            action={(
              <button type="button" className="mvp-button" onClick={() => openMembershipModal()}>
                Add membership
              </button>
            )}
          />
        )}
      </MvpCard>
    );
  };

  const renderSection = () => {
    if (!data) return null;
    const activity = data.activity || {};
    switch (tab) {
      case 'capabilities':
        return renderCapabilitiesSection();
      case 'memberships':
        return renderMembershipsSection();
      case 'work-history':
        return <MvpCard title="Work history"><pre>{JSON.stringify((activity as any).workHistory || activity, null, 2)}</pre></MvpCard>;
      case 'portal-access':
        return (
          <>
          <PortalAccessCard
            member={data.member}
            portalSessions={data.portalSessions}
            settings={data.settings}
            onRefresh={reload}
            onFeedback={setFeedback}
            verificationActions={(
              <VerificationActions
                member={data.member}
                loading={verificationSubmitting}
                onAction={handleVerificationMutation}
                compact
                capabilityCount={data.capabilities.length || data.member.capabilityTypes?.length || 0}
                membershipCount={data.memberships.length}
              />
            )}
            verificationBusy={verificationSubmitting}
          />
          <TalentPasswordManagementCard
            member={data.member}
            canManageCredentials={canManageTalentCredentials}
            onRefresh={reload}
            onFeedback={setFeedback}
          />
          </>
        );
      case 'security':
        return <MvpCard title="Security"><pre>{JSON.stringify((activity as any).securityEvents || activity, null, 2)}</pre></MvpCard>;
      case 'audit-history':
        return <MvpCard title="Audit history"><pre>{JSON.stringify((activity as any).auditEvents || activity, null, 2)}</pre></MvpCard>;
      case 'overview':
      default:
        return (
          <>
            <MvpCard title="Global profile">
              <KeyValueGrid items={[
                { label: 'Email', value: formatEmptyValue(data.member.email) },
                { label: 'Phone', value: formatEmptyValue(data.member.phone) },
                { label: 'Company', value: formatEmptyValue(data.member.companyName) },
                { label: 'Member type', value: formatEmptyValue(data.member.memberType) },
                { label: 'Status', value: <StatusBadge status={data.member.globalStatus} /> },
                { label: 'Verification', value: <VerificationStatusBadge status={data.member.verificationStatus} /> },
                { label: 'Capabilities', value: formatList(data.member.capabilityTypes) },
                { label: 'Networks', value: formatList(data.member.networkTypesJson) },
                { label: 'Created', value: formatDateTime(data.member.createdAt) },
              ]} />
            </MvpCard>
            <TalentVerificationCard
              member={data.member}
              capabilityCount={data.capabilities.length || data.member.capabilityTypes?.length || 0}
              membershipCount={data.memberships.length}
              loading={verificationSubmitting}
              onAction={handleVerificationMutation}
            />
            <MvpCard title="Capabilities">
              <p>{formatList(data.member.capabilityTypes)}</p>
            </MvpCard>
            <MvpCard title="Instance memberships">
              <p>{data.memberships.length ? `${data.memberships.length} membership${data.memberships.length === 1 ? '' : 's'} connected.` : 'No memberships yet.'}</p>
            </MvpCard>
          </>
        );
    }
  };

  return (
    <MvpTalentLayout
      title={data?.member.displayName || `Talent ${memberId}`}
      subtitle="Global profile, capabilities, memberships, portal access, and cross-instance activity."
      actions={
        <InlineActions>
          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => reload()}>Refresh</button>
          {data ? (
            <VerificationActions
              member={data.member}
              loading={verificationSubmitting}
              onAction={handleVerificationMutation}
              compact
              capabilityCount={data.capabilities.length || data.member.capabilityTypes?.length || 0}
              membershipCount={data.memberships.length}
            />
          ) : null}
          <button
            type="button"
            className="mvp-button mvp-button--ghost"
            onClick={() => setStatusDialog({ action: data?.member.globalStatus === 'suspended' ? 'reactivate' : 'suspend' })}
          >
            {data?.member.globalStatus === 'suspended' ? 'Reactivate' : 'Suspend'}
          </button>
          <button type="button" className="mvp-button" onClick={() => setStatusDialog({ action: 'revokePortal' })}>
            Revoke portal
          </button>
        </InlineActions>
      }
    >
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      {loading ? <LoadingState label="Loading talent profile..." /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <MvpCard className="mvp-member-hero">
            <div className="mvp-member-hero__banner-wrap">
              {data.member.bannerImageUrl && !bannerImageBroken ? (
                <img
                  className="mvp-member-hero__banner-image"
                  src={buildCoreAssetUrl(data.member.bannerImageUrl)}
                  alt={`${data.member.displayName} banner`}
                  onError={() => setBannerImageBroken(true)}
                  style={renderMemberBannerPosition(data.member.bannerFocalPointJson)}
                />
              ) : (
                <div className="mvp-member-hero__banner-fallback" aria-label="No banner image" />
              )}
              <div className="mvp-member-hero__banner-overlay" />
              <div className="mvp-member-hero__banner-actions">
                <button
                  type="button"
                  className="mvp-button mvp-button--ghost"
                  onClick={() => setImageEditorMode('banner')}
                  disabled={Boolean(imageActionBusy)}
                >
                  Change banner
                </button>
                {data.member.bannerImageUrl ? (
                  <button
                    type="button"
                    className="mvp-button mvp-button--ghost"
                    onClick={() => handleMemberImageUpdate(
                      'banner',
                      () => platformCoreNetworkApi.removeMemberBannerImage(memberId),
                      'Banner image removed.'
                    )}
                    disabled={Boolean(imageActionBusy)}
                  >
                    Remove banner
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mvp-member-hero__content">
              <div className="mvp-member-hero__avatar-stack">
                <div className="mvp-avatar mvp-avatar--hero">
                  {data.member.profileImageUrl && !profileImageBroken ? (
                    <img
                      src={buildCoreAssetUrl(data.member.profileImageUrl)}
                      alt={data.member.displayName}
                      onError={() => setProfileImageBroken(true)}
                    />
                  ) : (
                    toInitials(data.member.displayName, data.member.companyName)
                  )}
                </div>
                <div className="mvp-member-hero__avatar-actions">
                  <button
                    type="button"
                    className="mvp-button mvp-button--ghost"
                    onClick={() => setImageEditorMode('profile')}
                    disabled={Boolean(imageActionBusy)}
                  >
                    Change photo
                  </button>
                  {data.member.profileImageUrl ? (
                    <button
                      type="button"
                      className="mvp-button mvp-button--ghost"
                      onClick={() => handleMemberImageUpdate(
                        'profile',
                        () => platformCoreNetworkApi.removeMemberProfileImage(memberId),
                        'Profile image removed.'
                      )}
                      disabled={Boolean(imageActionBusy)}
                    >
                      Remove photo
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="mvp-member-hero__identity">
                <h3 style={{ margin: 0 }}>{data.member.displayName}</h3>
                <p style={{ margin: '6px 0 0' }}>
                  {formatEmptyValue(data.member.companyName || data.member.email || '')}
                </p>
                <div className="mvp-member-hero__meta">
                  <StatusBadge status={data.member.globalStatus} />
                  <span className="mvp-member-hero__verification">Verification <VerificationStatusBadge status={data.member.verificationStatus} /></span>
                  {data.member.memberType ? <span>{data.member.memberType}</span> : null}
                  {data.member.portalStatus ? <span>Portal: {data.member.portalStatus}</span> : null}
                </div>
              </div>
            </div>
          </MvpCard>
          <MvpCard title="Sections">
            <InlineActions>
              {tabs.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`mvp-button ${tab === item ? '' : 'mvp-button--ghost'}`.trim()}
                  onClick={() => setSearchParams({ tab: item })}
                >
                  {item.replace(/-/g, ' ')}
                </button>
              ))}
            </InlineActions>
          </MvpCard>
          {renderSection()}
        </>
      ) : null}

      <ConfirmDialog
        open={Boolean(statusDialog)}
        title={statusDialog?.action === 'revokePortal' ? 'Revoke portal access' : 'Update talent status'}
        description="This action is recorded on the global platform audit log."
        confirmLabel={statusDialog?.action === 'revokePortal' ? 'Revoke portal access' : 'Apply change'}
        confirmTone={statusDialog?.action === 'suspend' || statusDialog?.action === 'revokePortal' ? 'danger' : 'default'}
        onClose={() => setStatusDialog(null)}
        onConfirm={async ({ reason }) => {
          if (!statusDialog) return;
          await handleStatusMutation(statusDialog.action, reason);
        }}
      />

      {capabilityModalOpen ? (
        <div className="mvp-dialog-backdrop" role="presentation" onClick={closeCapabilityModal}>
          <div className="mvp-dialog mvp-dialog--wide mvp-capability-dialog" role="dialog" aria-modal="true" aria-labelledby="capability-dialog-title" onClick={(event) => event.stopPropagation()}>
            <div className="mvp-dialog__header">
              <div>
                <h3 id="capability-dialog-title">{capabilityForm.capabilityId ? 'Edit talent capability' : 'Add talent capability'}</h3>
                <p>Define the service this talent can perform and how it should be matched to work.</p>
              </div>
              <button type="button" className="mvp-button mvp-button--ghost" aria-label="Close capability dialog" onClick={closeCapabilityModal} disabled={capabilitySubmitting}>×</button>
            </div>
            <div className="mvp-info-panel">
              This capability applies to the talent’s global profile. It determines what work they may be matched with. Instance membership still controls where they may receive work.
            </div>
            {capabilityError ? <FeedbackBanner tone="error" message={capabilityError} /> : null}
            {!capabilityForm.capabilityId && data?.capabilities.some((capability) => !capability.membershipId && capability.isActive === false) ? (
              <div className="mvp-info-panel mvp-inactive-capabilities" aria-label="Inactive capabilities on this talent">
                <strong>Inactive capabilities can be reused.</strong>
                <p>Reactivate an inactive global capability or edit it instead of creating a duplicate record.</p>
                <div className="mvp-inactive-capabilities__list">
                  {data.capabilities
                    .filter((capability) => !capability.membershipId && capability.isActive === false)
                    .map((capability) => (
                      <div key={capability.id} className="mvp-inactive-capabilities__item">
                        <span>{getCapabilityLabel(capability.capabilityType)}</span>
                        <InlineActions>
                          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => openCapabilityModal(capability)} disabled={capabilitySubmitting}>
                            Edit existing
                          </button>
                          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => updateCapabilityActiveState(capability, true)} disabled={capabilitySubmitting}>
                            Reactivate
                          </button>
                        </InlineActions>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
            <form className="mvp-capability-form" onSubmit={submitCapability}>
              <section className="mvp-form-section">
                <h4>Capability</h4>
                <div className="mvp-form-grid">
                  <FieldGroup label="Capability *">
                    <select
                      value={capabilityForm.capabilityType}
                      onChange={(event) => {
                        const definition = findCapabilityDefinition(event.target.value);
                        setCapabilityForm((current) => ({
                          ...current,
                          capabilityType: event.target.value,
                          capabilityGroup: definition?.group || '',
                          rateType: definition?.defaultRateType || current.rateType,
                        }));
                      }}
                      aria-describedby="capability-help capability-error"
                      required
                      disabled={capabilitySubmitting || Boolean(capabilityForm.capabilityId)}
                    >
                      <option value="">Select a capability</option>
                      {CAPABILITY_GROUPS.map((group) => (
                        <optgroup key={group.value} label={group.label}>
                          {CAPABILITY_CATALOGUE.filter((capability) => capability.group === group.value).map((capability) => {
                            const duplicate = data?.capabilities.find((existing) => existing.capabilityType === capability.value && existing.id !== capabilityForm.capabilityId && !existing.membershipId);
                            return (
                              <option key={capability.value} value={capability.value} disabled={Boolean(duplicate && !capabilityForm.capabilityId)}>
                                {capability.label}{duplicate ? duplicate.isActive === false ? ' (inactive - edit or reactivate)' : ' (already added)' : ''}
                              </option>
                            );
                          })}
                        </optgroup>
                      ))}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Category">
                    <input value={selectedCapabilityDefinition?.groupLabel || ''} readOnly aria-readonly="true" />
                  </FieldGroup>
                  <div id="capability-help" className="mvp-field-help mvp-form-grid__actions">
                    {selectedCapabilityDefinition?.description || 'Choose a capability to see what it means and which matching details can be configured.'}
                  </div>
                  <label className="mvp-checkbox mvp-form-grid__actions">
                    <input
                      type="checkbox"
                      checked={capabilityForm.isActive}
                      onChange={(event) => setCapabilityForm((current) => ({ ...current, isActive: event.target.checked }))}
                      disabled={capabilitySubmitting}
                    />
                    <span>Active for matching and assignment</span>
                  </label>
                  {!capabilityForm.isActive ? <div className="mvp-field-help mvp-form-grid__actions">Inactive capabilities will not be used for matching or assignment.</div> : null}
                </div>
              </section>

              <section className="mvp-form-section">
                <h4>Service details</h4>
                <div className="mvp-form-grid">
                  <FieldGroup label="Service name override">
                    <input value={capabilityForm.displayName} onChange={(event) => setCapabilityForm((current) => ({ ...current, displayName: event.target.value }))} disabled={capabilitySubmitting} />
                  </FieldGroup>
                  <FieldGroup label="Customer-facing description">
                    <textarea value={capabilityForm.description} onChange={(event) => setCapabilityForm((current) => ({ ...current, description: event.target.value }))} rows={3} disabled={capabilitySubmitting} />
                  </FieldGroup>
                  <FieldGroup label="Internal notes">
                    <textarea value={capabilityForm.notes} onChange={(event) => setCapabilityForm((current) => ({ ...current, notes: event.target.value }))} rows={3} disabled={capabilitySubmitting} />
                  </FieldGroup>
                </div>
              </section>

              {selectedCapabilityDefinition?.supportsTurnaround ? (
                <section className="mvp-form-section">
                  <h4>Turnaround and availability</h4>
                  <div className="mvp-form-grid">
                    <FieldGroup label="Standard turnaround">
                      <input type="number" min="0" value={capabilityForm.turnaroundValue} onChange={(event) => setCapabilityForm((current) => ({ ...current, turnaroundValue: event.target.value }))} disabled={capabilitySubmitting} />
                    </FieldGroup>
                    <FieldGroup label="Turnaround unit">
                      <select value={capabilityForm.turnaroundUnit} onChange={(event) => setCapabilityForm((current) => ({ ...current, turnaroundUnit: event.target.value as typeof current.turnaroundUnit }))} disabled={capabilitySubmitting}>
                        {TURNAROUND_UNIT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </FieldGroup>
                    <FieldGroup label="Minimum lead time">
                      <input type="number" min="0" value={capabilityForm.minimumLeadTime} onChange={(event) => setCapabilityForm((current) => ({ ...current, minimumLeadTime: event.target.value }))} disabled={capabilitySubmitting} />
                    </FieldGroup>
                    <FieldGroup label="Maximum active jobs">
                      <input type="number" min="0" value={capabilityForm.maximumActiveJobs} onChange={(event) => setCapabilityForm((current) => ({ ...current, maximumActiveJobs: event.target.value }))} disabled={capabilitySubmitting} />
                    </FieldGroup>
                    {selectedCapabilityDefinition.supportsRush ? (
                      <label className="mvp-checkbox mvp-form-grid__actions">
                        <input type="checkbox" checked={capabilityForm.rushAvailable} onChange={(event) => setCapabilityForm((current) => ({ ...current, rushAvailable: event.target.checked }))} disabled={capabilitySubmitting} />
                        <span>Rush service available</span>
                      </label>
                    ) : null}
                    {selectedCapabilityDefinition.supportsRush && capabilityForm.rushAvailable ? (
                      <FieldGroup label="Rush turnaround">
                        <input type="number" min="0" value={capabilityForm.rushValue} onChange={(event) => setCapabilityForm((current) => ({ ...current, rushValue: event.target.value }))} disabled={capabilitySubmitting} />
                      </FieldGroup>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {selectedCapabilityDefinition?.supportsPricing ? (
                <section className="mvp-form-section">
                  <h4>Pricing</h4>
                  <div className="mvp-form-grid">
                    <label className="mvp-checkbox mvp-form-grid__actions">
                      <input type="checkbox" checked={capabilityForm.pricingEnabled} onChange={(event) => setCapabilityForm((current) => ({ ...current, pricingEnabled: event.target.checked }))} disabled={capabilitySubmitting} />
                      <span>Pricing enabled</span>
                    </label>
                    <FieldGroup label="Rate type">
                      <select value={capabilityForm.rateType} onChange={(event) => setCapabilityForm((current) => ({ ...current, rateType: event.target.value }))} disabled={capabilitySubmitting || !capabilityForm.pricingEnabled}>
                        {CAPABILITY_RATE_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </FieldGroup>
                    <FieldGroup label="Rate amount">
                      <input type="number" min="0" step="0.01" value={capabilityForm.rateAmount} onChange={(event) => setCapabilityForm((current) => ({ ...current, rateAmount: event.target.value }))} disabled={capabilitySubmitting || !capabilityForm.pricingEnabled} />
                    </FieldGroup>
                    <FieldGroup label="Currency">
                      <input value={capabilityForm.currency} onChange={(event) => setCapabilityForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))} disabled={capabilitySubmitting || !capabilityForm.pricingEnabled} />
                    </FieldGroup>
                    <FieldGroup label="Minimum charge">
                      <input type="number" min="0" step="0.01" value={capabilityForm.minimumCharge} onChange={(event) => setCapabilityForm((current) => ({ ...current, minimumCharge: event.target.value }))} disabled={capabilitySubmitting || !capabilityForm.pricingEnabled} />
                    </FieldGroup>
                  </div>
                </section>
              ) : null}

              {selectedCapabilityDefinition ? (
                <section className="mvp-form-section">
                  <h4>Capability-specific fields</h4>
                  {selectedCapabilityDefinition.supportsFileTypes ? (
                    <div className="mvp-form-section__cluster">
                      <p className="mvp-field-help">Artwork and design capabilities can define accepted source and output file formats.</p>
                      <fieldset className="mvp-checkbox-grid"><legend>Accepted source file types</legend>{FILE_TYPE_OPTIONS.map((option) => <label key={option} className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.acceptedFileTypes.includes(option)} onChange={() => setCapabilityForm((current) => ({ ...current, acceptedFileTypes: toggleSelection(current.acceptedFileTypes, option) }))} disabled={capabilitySubmitting} /><span>{option}</span></label>)}</fieldset>
                      <fieldset className="mvp-checkbox-grid"><legend>Output file types</legend>{FILE_TYPE_OPTIONS.map((option) => <label key={option} className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.outputFileTypes.includes(option)} onChange={() => setCapabilityForm((current) => ({ ...current, outputFileTypes: toggleSelection(current.outputFileTypes, option) }))} disabled={capabilitySubmitting} /><span>{option}</span></label>)}</fieldset>
                      <label className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.colourMatchingSupported} onChange={(event) => setCapabilityForm((current) => ({ ...current, colourMatchingSupported: event.target.checked }))} disabled={capabilitySubmitting} /><span>Colour matching supported</span></label>
                      <label className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.complexArtworkSupported} onChange={(event) => setCapabilityForm((current) => ({ ...current, complexArtworkSupported: event.target.checked }))} disabled={capabilitySubmitting} /><span>Complex artwork supported</span></label>
                    </div>
                  ) : null}
                  {selectedCapabilityDefinition.supportsDecorationMethods ? (
                    <div className="mvp-form-section__cluster">
                      <fieldset className="mvp-checkbox-grid"><legend>Supported decoration methods</legend>{DECORATION_METHOD_OPTIONS.map((option) => <label key={option} className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.decorationMethods.includes(option)} onChange={() => setCapabilityForm((current) => ({ ...current, decorationMethods: toggleSelection(current.decorationMethods, option) }))} disabled={capabilitySubmitting} /><span>{option}</span></label>)}</fieldset>
                      <div className="mvp-form-grid"><FieldGroup label="Minimum quantity"><input type="number" min="0" value={capabilityForm.minimumQuantity} onChange={(event) => setCapabilityForm((current) => ({ ...current, minimumQuantity: event.target.value }))} disabled={capabilitySubmitting} /></FieldGroup><FieldGroup label="Maximum quantity"><input type="number" min="0" value={capabilityForm.maximumQuantity} onChange={(event) => setCapabilityForm((current) => ({ ...current, maximumQuantity: event.target.value }))} disabled={capabilitySubmitting} /></FieldGroup><FieldGroup label="Production location"><input value={capabilityForm.productionLocation} onChange={(event) => setCapabilityForm((current) => ({ ...current, productionLocation: event.target.value }))} disabled={capabilitySubmitting} /></FieldGroup></div>
                      <label className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.sampleProductionAvailable} onChange={(event) => setCapabilityForm((current) => ({ ...current, sampleProductionAvailable: event.target.checked }))} disabled={capabilitySubmitting} /><span>Sample production available</span></label>
                    </div>
                  ) : null}
                  {selectedCapabilityDefinition.supportsLogisticsOptions ? (
                    <div className="mvp-form-section__cluster">
                      <fieldset className="mvp-checkbox-grid"><legend>Logistics services</legend>{LOGISTICS_SERVICE_OPTIONS.map((option) => <label key={option} className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.serviceOptions.includes(option)} onChange={() => setCapabilityForm((current) => ({ ...current, serviceOptions: toggleSelection(current.serviceOptions, option) }))} disabled={capabilitySubmitting} /><span>{option}</span></label>)}</fieldset>
                      <FieldGroup label="Maximum order volume"><input type="number" min="0" value={capabilityForm.maximumOrderVolume} onChange={(event) => setCapabilityForm((current) => ({ ...current, maximumOrderVolume: event.target.value }))} disabled={capabilitySubmitting} /></FieldGroup>
                    </div>
                  ) : null}
                  {selectedCapabilityDefinition.supportsDataEntryOptions ? (
                    <div className="mvp-form-section__cluster">
                      <fieldset className="mvp-checkbox-grid"><legend>Supported task types</legend>{DATA_ENTRY_TASK_OPTIONS.map((option) => <label key={option} className="mvp-checkbox"><input type="checkbox" checked={capabilityForm.taskTypes.includes(option)} onChange={() => setCapabilityForm((current) => ({ ...current, taskTypes: toggleSelection(current.taskTypes, option) }))} disabled={capabilitySubmitting} /><span>{option}</span></label>)}</fieldset>
                      <div className="mvp-form-grid"><FieldGroup label="Hourly capacity"><input type="number" min="0" value={capabilityForm.hourlyCapacity} onChange={(event) => setCapabilityForm((current) => ({ ...current, hourlyCapacity: event.target.value }))} disabled={capabilitySubmitting} /></FieldGroup><FieldGroup label="Software experience"><input value={capabilityForm.softwareExperience} onChange={(event) => setCapabilityForm((current) => ({ ...current, softwareExperience: event.target.value }))} disabled={capabilitySubmitting} /></FieldGroup></div>
                    </div>
                  ) : null}
                </section>
              ) : null}

              <div className="mvp-dialog__actions mvp-dialog__actions--sticky">
                <button type="button" className="mvp-button mvp-button--ghost" onClick={closeCapabilityModal} disabled={capabilitySubmitting}>
                  Cancel
                </button>
                <button type="submit" className="mvp-button" disabled={capabilitySubmitting || !selectedCapabilityDefinition}>
                  {capabilitySubmitting ? 'Saving capability...' : capabilityForm.capabilityId ? 'Save capability' : 'Add capability'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {membershipModalOpen ? (
        <div className="mvp-dialog-backdrop" role="presentation" onClick={() => !membershipSubmitting && setMembershipModalOpen(false)}>
          <div className="mvp-dialog mvp-dialog--wide" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>{membershipForm.membershipId ? 'Edit membership' : 'Add membership'}</h3>
            <p>Memberships connect this global profile to a specific instance. They do not duplicate the global member.</p>
            {membershipError ? <FeedbackBanner tone="error" message={membershipError} /> : null}
            <form className="mvp-form-grid" onSubmit={submitMembership}>
              <FieldGroup label="Instance *">
                <select
                  value={membershipForm.instanceId}
                  onChange={(event) => setMembershipForm((current) => ({ ...current, instanceId: event.target.value }))}
                  required
                  disabled={Boolean(membershipForm.membershipId)}
                >
                  <option value="">Select instance</option>
                  {data?.instances.rows.map((instance) => (
                    <option key={instance.id} value={String(instance.id)}>
                      {instance.instanceName || instance.instanceUniqueId || `Instance ${instance.id}`}
                    </option>
                  ))}
                </select>
              </FieldGroup>
              <FieldGroup label="Status *">
                <select value={membershipForm.status} onChange={(event) => setMembershipForm((current) => ({ ...current, status: event.target.value }))}>
                  {MEMBERSHIP_STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FieldGroup>
              <FieldGroup label="Role">
                <input
                  list="membership-role-options"
                  value={membershipForm.role}
                  onChange={(event) => setMembershipForm((current) => ({ ...current, role: event.target.value }))}
                />
                <datalist id="membership-role-options">
                  {MEMBERSHIP_ROLE_OPTIONS.map((option) => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
              </FieldGroup>
              <FieldGroup label="Visibility">
                <select value={membershipForm.visibility} onChange={(event) => setMembershipForm((current) => ({ ...current, visibility: event.target.value }))}>
                  {MEMBERSHIP_VISIBILITY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FieldGroup>
              <FieldGroup label="Allowed request types">
                <div className="mvp-checkbox-grid">
                  {REQUEST_TYPE_OPTIONS.map((option) => (
                    <label key={option} className="mvp-checkbox">
                      <input
                        type="checkbox"
                        checked={membershipForm.allowedRequestTypesJson.includes(option)}
                        onChange={() => setMembershipForm((current) => ({
                          ...current,
                          allowedRequestTypesJson: toggleSelection(current.allowedRequestTypesJson, option),
                        }))}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </FieldGroup>
              <FieldGroup label="Rate type">
                <select value={membershipForm.rateType} onChange={(event) => setMembershipForm((current) => ({ ...current, rateType: event.target.value }))}>
                  <option value="">Not set</option>
                  {MEMBERSHIP_RATE_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FieldGroup>
              <FieldGroup label="Rate amount">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={membershipForm.rateAmount}
                  onChange={(event) => setMembershipForm((current) => ({ ...current, rateAmount: event.target.value }))}
                />
              </FieldGroup>
              <FieldGroup label="Rate currency">
                <input
                  value={membershipForm.rateCurrency}
                  onChange={(event) => setMembershipForm((current) => ({ ...current, rateCurrency: event.target.value.toUpperCase() }))}
                />
              </FieldGroup>
              <FieldGroup label="Administrative reason">
                <textarea
                  value={membershipForm.reason}
                  onChange={(event) => setMembershipForm((current) => ({ ...current, reason: event.target.value }))}
                  rows={3}
                />
              </FieldGroup>
              <div className="mvp-dialog__actions mvp-form-grid__actions">
                <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setMembershipModalOpen(false)} disabled={membershipSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="mvp-button" disabled={membershipSubmitting || !membershipForm.instanceId}>
                  {membershipSubmitting ? 'Saving...' : membershipForm.membershipId ? 'Save membership' : 'Add membership'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {data && imageEditorMode ? (
        <MemberImageEditor
          open
          mode={imageEditorMode}
          memberName={data.member.displayName}
          currentImageUrl={imageEditorMode === 'profile'
            ? (data.member.profileImageUrl ? buildCoreAssetUrl(data.member.profileImageUrl) : null)
            : (data.member.bannerImageUrl ? buildCoreAssetUrl(data.member.bannerImageUrl) : null)}
          onClose={() => {
            if (!imageActionBusy) {
              setImageEditorMode(null);
            }
          }}
          onSave={({ file, focalPoint }) => handleMemberImageUpdate(
            imageEditorMode,
            () => imageEditorMode === 'profile'
              ? platformCoreNetworkApi.uploadMemberProfileImage(memberId, file)
              : platformCoreNetworkApi.uploadMemberBannerImage(memberId, file, focalPoint || null),
            imageEditorMode === 'profile' ? 'Profile image updated.' : 'Banner image updated.'
          )}
        />
      ) : null}
    </MvpTalentLayout>
  );
};

export default TalentDetailPage;
